import { PressEvent } from '@react-types/shared';
import React, {
    ElementType,
    forwardRef,
    HTMLAttributeAnchorTarget,
    HTMLAttributes,
    JSXElementConstructor,
    MouseEvent,
    ReactNode,
    useCallback,
} from 'react';
import { mergeProps, useHover, useLink as useAriaLink, AriaLinkOptions, HoverProps } from 'react-aria';
import { useLogger, Loggable } from '@direct-frontend/modules-logger';

import { useObjectRef } from '../../hooks/useObjectRef/useObjectRef';
import { cn } from '../../lib/css/className/className';
import './Link.css';
import { TestProps } from '../../internal/types/testTypes';
import { useFocusOptions } from '../../hooks/useFocusOptions';

export type LinkColor =
    | 'default'
    | 'main'
    | 'supplementary'
    | 'placeholder'
    | 'dotted'
    | 'underline-supplementary'
    | 'underline';

export type LinkProps = Omit<AriaLinkOptions, 'elementType' | 'isDisabled'> &
    TestProps &
    HoverProps &
    Loggable & {
        /**
         * Адрес перехода для ссылки
         */
        href?: string;
        /**
         * Содержимое ссылки
         */
        children?: ReactNode;
        /**
         * Цветовое оформление ссылки
         * @default default
         */
        color?: LinkColor;

        /**
         * Обработчик нажатия на ссылку
         */
        onClick?: (e: PressEvent) => void;

        /**
         * Признак отключенной сcылки
         */
        isDisabled?: boolean;
        /**
         * Дополнительный класс для ссылки
         */
        className?: string;
        /**
         * HTML-атрибут `title`
         */
        title?: string;

        /**
         * HTML-атрибут `target`
         */
        target?: HTMLAttributeAnchorTarget;

        /**
         * HTML-атрибут `rel`
         */
        rel?: string;

        /**
         * HTML-атрибут `tabIndex`. Определяет последовательность перехода между ссылками при нажатии на кнопку Tab
         */
        tabIndex?: number;

        /**
         * HTML-тег или компонент для рендера активной ссылки
         */
        elementType?: ElementType | JSXElementConstructor<{ href?: string }>;

        /**
         * Обработчик нажатия на ссылку с defaultPrevented
         */
        onClickNative?: (e: MouseEvent<HTMLAnchorElement>) => void;

        /* Поддержка произвольных dom атрибутов для ссылки */
        domAttrs?: HTMLAttributes<HTMLElement> & { [key: string]: unknown };
    };

const cnLink = cn('Link');

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(props, forwardedRef) {
    const ref = useObjectRef(forwardedRef);
    const {
        color = 'default',
        onClick,
        onClickNative,
        onPress,
        elementType,
        href,
        title,
        target,
        rel,
        tabIndex,
        domAttrs,
        logNode,
        'data-testid': dataTestId = 'Link',
        ...restProps
    } = props;
    const { LogProvider, logClick } = useLogger('Link', logNode);
    const ElementType = props.isDisabled || !href ? 'span' : (elementType ?? 'a');

    const onClickHandler = useCallback(
        (event: PressEvent) => {
            logClick();

            if (onPress) {
                onPress(event);
            } else {
                onClick?.(event);
            }
        },
        [logClick, onClick, onPress],
    );

    const defaultElementType = props.isDisabled || !href ? 'span' : 'a';
    const { linkProps, isPressed } = useAriaLink(
        {
            ...restProps,
            href,
            onPress: onClickHandler,
            elementType: typeof ElementType === 'string' ? ElementType : defaultElementType,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
            // @ts-expect-error TODO DIRECT-222878 Добавить причину
            onClick: onClickNative,
        },
        ref,
    );
    const { hoverProps, isHovered } = useHover(props);
    // TODO DIRECT-263743 Поресерчить фокусы в модалке на мобиле, после этого если нужно - откатить
    const { focusProps, isFocusVisible } = useFocusOptions(props);

    return (
        <LogProvider>
            <ElementType
                {...mergeProps(linkProps, focusProps, hoverProps)}
                {...domAttrs}
                ref={ref}
                href={href}
                title={title}
                target={target}
                rel={rel}
                tabIndex={tabIndex}
                data-testid={dataTestId}
                className={cnLink(
                    {
                        color,
                        hovered: isHovered,
                        pressed: isPressed,
                        focused: isFocusVisible,
                        disabled: props.isDisabled,
                    },
                    [props.className],
                )}
            >
                {props.children}
            </ElementType>
        </LogProvider>
    );
});
